import { useEffect, useMemo, useState } from 'react';

import { Link } from 'react-router-dom';

import { ArrowRight, BadgeCheck, ClipboardList, Gift, Package, Plus, RefreshCw, TrendingUp, Wallet } from 'lucide-react';

import axiosClient from '../../api/axiosClient';

import { useLanguage } from '../../contexts/LanguageContext';

import '../../styles/Marketing/field-sales-dashboard.css';



// English fallbacks until fieldSalesDashboard keys are added to your locale JSON files.

// All user-facing strings resolve through the shared language provider.

const FALLBACK = {

    eyebrow: 'LMENU / FIELD SALES', title: 'Sales dashboard', description: 'Visits, product sales, promotional savings and commission tracking.',

    refresh: 'Refresh', myVisits: 'My visits', newRegistration: 'New registration', retry: 'Retry', loading: 'Loading dashboard…',

    unableToLoad: 'Unable to load sales dashboard.', metrics: 'Sales metrics', registeredVisits: 'Registered visits',

    approved: 'approved', awaitingReview: 'awaiting review', qrProducts: 'QR products ordered', freeUnitsNote: 'promotional free units',

    estimatedValue: 'Estimated sales value', estimatedValueNote: 'Products after discount + subscription',

    earnedCommission: 'Earned commission', projected: 'Projected',

    breakdown: 'Sales and promotion breakdown', breakdownNote: 'Estimated amounts from saved quantities and current catalog prices.',

    productsBefore: 'Products before promotion', promotion: 'Buy 9, get 1 free', productsAfter: 'QR products after promotion',

    subscriptions: 'Subscriptions (including custom or none)', combined: 'Combined estimated sales',

    missingPrice: '{{count}} units have no catalog price and are excluded from monetary estimates and promotion eligibility.',

    commissionRules: 'Agent commission rules', commissionNote: 'Commission applies to paid units; promotional free units earn no commission.',

    productPrice: 'Product price', projectedCommissions: 'Projected commissions', earnedPaid: 'Earned (approved + payment paid)',

    otherPricesNote: "Other product prices don't have defined commission rates yet. No subscription commission is assumed. Sales and earnings are estimates until verified; accepted quotes and historical prices are not stored in these registration items.",

    visitsTitle: 'Visits and estimated earnings', visitsDescription: 'Select any visit to see its individual product breakdown.',

    noVisits: 'No registrations yet. Create your first visit to get started.', establishment: 'Establishment', status: 'Status',

    units: 'Units', free: 'Free', qrTotal: 'QR total', subscription: 'Subscription', commission: 'Commission',

    payment: 'Payment', earned: 'Earned', visit: 'Visit', breakdownSuffix: 'Product breakdown', product: 'Product',

    qty: 'Qty', unitPrice: 'Unit price', paidAmount: 'Paid amount', notAvailable: '—',

    statusLabels: { approved: 'Approved', submitted: 'Submitted', pending: 'Pending', rejected: 'Rejected', paid: 'Paid', unpaid: 'Unpaid', none: 'None', monthly: 'Monthly', yearly: 'Yearly', custom: 'Custom', pending_review: 'Awaiting review' },



};



const getDashboardText = (t, key, fallback, replacements = {}) => {

  const translationKey = `fieldSalesDashboard.${key}`;

  const translated = typeof t === 'function' ? t(translationKey, replacements) : null;

  const source = translated && translated !== translationKey ? translated : fallback;

  return String(source ?? '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) =>

    replacements[name] === undefined ? `{{${name}}}` : String(replacements[name]));

};



const money = (value) => `${Number(value || 0).toLocaleString('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DH`;

const showDate = (value, language) => value && !Number.isNaN(Date.parse(value))

  ? new Date(value).toLocaleDateString(language === 'ar' ? 'ar-MA' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' })

  : '—';



export default function FieldSalesDashboard() {

  const { t } = useLanguage();

  const tr = useMemo(() => {

    const labels = {};

    for (const [key, fallback] of Object.entries(FALLBACK)) {

      if (key === 'statusLabels') continue;

      labels[key] = key === 'missingPrice'

        ? (count) => getDashboardText(t, key, fallback, { count })

        : getDashboardText(t, key, fallback);

    }

    labels.statusLabels = Object.fromEntries(

      Object.entries(FALLBACK.statusLabels).map(([key, fallback]) => [

        key, getDashboardText(t, `statusLabels.${key}`, fallback),

      ]),

    );

    return labels;

  }, [t]);

  const translateStatus = (value) => {

    const key = String(value || '').toLowerCase();

    return tr.statusLabels[key] || (key ? getDashboardText(t, `statusLabels.${key}`, value) : tr.notAvailable);

  };



  const [report, setReport] = useState(null);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState('');

  const [refresh, setRefresh] = useState(0);

  const [selectedId, setSelectedId] = useState(null);



  useEffect(() => {

    const controller = new AbortController();

    (async () => {

      setLoading(true); setError('');

      try {

        const response = await axiosClient.get('/marketing/dashboard', { signal: controller.signal });

        if (!controller.signal.aborted) setReport(response.data?.data || null);

      } catch (e) {

        if (!controller.signal.aborted && e.code !== 'ERR_CANCELED') {

          setError(e?.response?.data?.message || e.message || tr.unableToLoad);

        }

      } finally {

        if (!controller.signal.aborted) setLoading(false);

      }

    })();

    return () => controller.abort();

  }, [refresh]);



  const s = report?.summary || {};

  const visits = report?.visits || [];

  const selected = useMemo(() => visits.find((v) => v.id === selectedId), [visits, selectedId]);

  const stats = [

    { icon: ClipboardList, label: tr.registeredVisits, value: s.visits ?? 0, note: `${s.approved ?? 0} ${tr.approved} · ${s.submitted ?? 0} ${tr.awaitingReview}` },

    { icon: Package, label: tr.qrProducts, value: s.product_units ?? 0, note: `${s.free_units ?? 0} ${tr.freeUnitsNote}` },

    { icon: TrendingUp, label: tr.estimatedValue, value: money(s.combined_total), note: tr.estimatedValueNote },

    { icon: Wallet, label: tr.earnedCommission, value: money(s.earned_commission), note: `${tr.projected}: ${money(s.projected_commission)}` },

  ];



  return <main className="fsd-shell">

    <header className="fsd-header"><div><span className="fsd-eyebrow"><BadgeCheck size={15} /> {tr.eyebrow}</span><h1>{tr.title}</h1><p>{tr.description}</p></div>

      <div className="fsd-actions"><button type="button" className="fsd-button secondary" disabled={loading} onClick={() => setRefresh((n) => n + 1)}><RefreshCw size={16} /> {tr.refresh}</button><Link className="fsd-button secondary" to="/marketing/visits">{tr.myVisits}</Link><Link className="fsd-button" to="/marketing/field-visits/new"><Plus size={16} /> {tr.newRegistration} <ArrowRight size={15} /></Link></div></header>

    {error && <div className="fsd-error" role="alert">{error} <button className="fsd-button secondary" type="button" onClick={() => setRefresh((n) => n + 1)}>{tr.retry}</button></div>}

    {loading ? <div className="fsd-empty">{tr.loading}</div> : !error && <>

      <section className="fsd-stats" aria-label={tr.metrics}>{stats.map(({ icon: Icon, label, value, note }) => <article className="fsd-stat" key={label}><Icon size={23} /><span className="fsd-label">{label}</span><strong>{value}</strong><small>{note}</small></article>)}</section>

      <div className="fsd-columns"><section className="fsd-panel"><h2>{tr.breakdown}</h2><p className="fsd-muted">{tr.breakdownNote}</p><div className="fsd-breakdown">

        <div className="fsd-line"><span>{tr.productsBefore}</span><strong>{money(s.product_subtotal)}</strong></div>

        <div className="fsd-line"><span><Gift size={14} style={{ verticalAlign: 'middle', color: '#b45309' }} /> {tr.promotion}</span><strong>−{money(s.promotion_discount)}</strong></div>

        <div className="fsd-line"><span>{tr.productsAfter}</span><strong>{money(s.product_total)}</strong></div>

        <div className="fsd-line"><span>{tr.subscriptions}</span><strong>{money(s.subscription_total)}</strong></div>

        <div className="fsd-line total"><span>{tr.combined}</span><strong>{money(s.combined_total)}</strong></div>

      </div>{Number(s.missing_price_units) > 0 && <p className="fsd-note">{tr.missingPrice(s.missing_price_units)}</p>}</section>

      <section className="fsd-panel"><h2>{tr.commissionRules}</h2><p className="fsd-muted">{tr.commissionNote}</p>

        <div className="fsd-rule"><span>{tr.productPrice} <b>70 DH</b></span><strong>+20 DH</strong></div>

        <div className="fsd-rule"><span>{tr.productPrice} <b>90 DH</b></span><strong>+30 DH</strong></div>

        <div className="fsd-line" style={{ marginTop: 18 }}><span>{tr.projectedCommissions}</span><strong>{money(s.projected_commission)}</strong></div>

        <div className="fsd-line"><span>{tr.earnedPaid}</span><strong>{money(s.earned_commission)}</strong></div>

        <p className="fsd-note">{tr.otherPricesNote}</p>

      </section></div>

      <section className="fsd-panel" style={{ marginTop: 17 }}><h2>{tr.visitsTitle}</h2><p className="fsd-muted">{tr.visitsDescription}</p>

        {visits.length === 0 ? <div className="fsd-empty">{tr.noVisits}</div> : <div className="fsd-tablewrap"><table className="fsd-table"><thead><tr><th>{tr.establishment}</th><th>{tr.status}</th><th>{tr.units}</th><th>{tr.free}</th><th>{tr.qrTotal}</th><th>{tr.subscription}</th><th>{tr.commission}</th></tr></thead><tbody>{visits.map((v) => <tr key={v.id}><td><button type="button" className="fsd-visitbutton" onClick={() => setSelectedId(selectedId === v.id ? null : v.id)}>{v.business_name || `${tr.visit} #${v.id}`}</button><small>{v.city || ''} · {showDate(v.created_at, typeof document !== 'undefined' ? document.documentElement.lang : 'en')}</small></td><td><span className="fsd-pill">{translateStatus(v.status)}</span><small>{tr.payment}: {translateStatus(v.payment_status)}</small></td><td>{v.units}</td><td>{v.free_units}</td><td>{money(v.product_total)}</td><td>{money(v.subscription_total)}<small>{translateStatus(v.subscription_period)}</small></td><td><strong>{money(v.projected_commission)}</strong><small>{tr.earned}: {money(v.earned_commission)}</small></td></tr>)}</tbody></table></div>}

        {selected && <div className="fsd-panel" style={{ boxShadow: 'none', marginTop: 16, background: '#fff8ee' }}><h2>{selected.business_name} · {tr.breakdownSuffix}</h2><div className="fsd-tablewrap"><table className="fsd-table"><thead><tr><th>{tr.product}</th><th>{tr.qty}</th><th>{tr.free}</th><th>{tr.unitPrice}</th><th>{tr.paidAmount}</th></tr></thead><tbody>{(selected.lines || []).map((line, index) => <tr key={line.id ?? index}><td>{line.name}</td><td>{line.quantity}</td><td>{line.free_quantity}</td><td>{line.unit_price == null ? tr.notAvailable : money(line.unit_price)}</td><td>{line.unit_price == null ? tr.notAvailable : money((line.quantity - line.free_quantity) * line.unit_price)}</td></tr>)}</tbody></table></div></div>}

      </section>

    </>}

  </main>;

}
